import { Switch, Route as _Route, Redirect } from 'react-router-dom';

import Profile from 'containers/Profile';
import Terminals from 'containers/Terminals';
import Users, { NewUser } from 'containers/Users';
import Sales from 'containers/Sales';
import { Services } from 'containers/Services';
import Update from 'containers/Update';
import { userContext } from 'contexts/UserContext';
import { Encashments } from 'containers/Encash';
import KnowledgeBase from 'containers/KnowledgeBase';
import UserGroups from 'containers/UserGroups';
import { Payment } from 'containers/Payment';
import { DCO } from 'containers/DCO';
import { Oracle } from 'containers/OraclePage';
import { UserLogs } from 'containers/UserLogs';
import StoreOrders from 'containers/StoreOrders';

import { useCheckBalance } from 'hooks/useCheckBalance';
import { UserInfo } from 'containers/UserInfo';
import { permissions, useHasPermission } from 'features/permissions';
import { Dashboard } from 'containers/Dashboard';
import { ComponentProps, ComponentType, useContext, useEffect } from 'react';
import { DashboardUser } from 'containers/DashboardUser';
import { useBrandFeatures } from 'hooks/useBrandFeatures';
import { LazyComponent } from 'utils/lazy';
import { Omnidesk } from 'containers/Omnidesk';
import { Staff } from 'containers/Staff';
import { useRedirectToServices } from 'hooks/useRedirectToServices';
import { SystemMaintenance } from 'containers/SystemMaintenance';
import { Migrations } from 'containers/Migrations';
import { isMigrationsHost } from 'config/migrations';

type LazyRoute = ComponentType<
  Omit<ComponentProps<typeof _Route>, 'component'> & {
    // В качестве компонента могут быть только Lazy старицы, используй lazyLoad
    component?: LazyComponent<never>;
  }
>;

const Route = _Route as unknown as LazyRoute;

function Routes() {
  const { user } = useContext(userContext);
  useCheckBalance();

  const redirectToServices = useRedirectToServices();
  const isBlocked = useHasPermission(permissions.isBlockedUser);
  const isAdmin = useHasPermission(permissions.isAdmin);
  const isGroupAdmin = useHasPermission(permissions.isGroupAdmin);

  const canReadAnyDevice = useHasPermission(permissions.can.readAnyDevice);

  useEffect(() => {
    if (isBlocked && !canReadAnyDevice) {
      redirectToServices();
    }
  }, [isBlocked, redirectToServices, canReadAnyDevice]);

  const features = useBrandFeatures();
  return (
    <>
      <Route path="/user/:userId" component={UserInfo} />
      <Switch>
        {permissions.isStaff(user) && (
          <Route path="/" exact>
            <Redirect to="/terminals" />
          </Route>
        )}
        {!isAdmin && <Route path="/" exact component={DashboardUser} />}
        {isAdmin && (
          <Route
            path="/"
            exact
            render={() => (
              <Redirect
                to={permissions.isSuperAdmin(user) ? 'dashboard' : '/users'}
              />
            )}
          />
        )}
        {permissions.isSuperAdmin(user) && (
          <Route path="/dashboard" component={Dashboard} />
        )}
        {isMigrationsHost() && permissions.isSuperAdmin(user) && (
          <Route path="/migrations" exact component={Migrations} />
        )}
        {permissions.isSuperAdmin(user) && (
          <Route
            path="/system/maintenance"
            exact
            component={SystemMaintenance}
          />
        )}
        {isAdmin && <Route path="/users" exact component={Users} />}
        {isAdmin && <Route path="/users/new" component={NewUser} />}
        <Route path="/(user)?/:userId?/profile" component={Profile} />
        <Route path="/(user)?/:userId?/terminals" component={Terminals} />
        <Route path="/(user)?/:userId?/storeOrders" component={StoreOrders} />
        <Route path="/(user)?/:userId?/sales" component={Sales} />
        <Route path="/(user)?/:userId?/encash" component={Encashments} />
        {features.services && permissions.not.isStaff(user) && (
          <Route path="/(user)?/:userId?/services" component={Services} />
        )}
        {features.hwt && <Route path="/(user)?/:userId?/hwt" component={DCO} />}
        {permissions.not.isStaff(user) && (
          <Route path="/(user)?/:userId?/oracle" component={Oracle} />
        )}
        {permissions.not.isStaff(user) && (
          <Route path="/(user)?/:userId?/staff" component={Staff} />
        )}
        <Route path="/(user)?/:userId?/userLogs" component={UserLogs} />
        {features.vendorSchool && (
          <Route path="/vendor" component={KnowledgeBase} />
        )}
        <Route path="/payment/:status?" component={Payment} />
        {isAdmin && <Route path="/update" component={Update} />}
        {isAdmin && isGroupAdmin ? (
          <Route path="/userGroups" component={UserGroups} />
        ) : undefined}
        {isAdmin && (
          <Route path="/user/:userId" exact component={DashboardUser} />
        )}
        {!isAdmin && <Route path="/" exact component={DashboardUser} />}
        {isGroupAdmin && features.omni && (
          <Route path="/omnidesk" exact component={Omnidesk} />
        )}
        <Route render={() => <Redirect to={isAdmin ? '/users' : '/'} />} />
      </Switch>
    </>
  );
}

export default Routes;
